import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Clock,
  MapPin,
  Phone,
} from "lucide-react";
import { IMAGES, WHAT_WE_CARRY } from "@/data/mockData";
import { SITE_CONFIG } from "@/data/siteConfig";

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-cream">
      <section data-testid="home-hero" className="py-10 md:py-16 lg:py-20">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-9 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <h1 className="display-serif text-[48px] sm:text-[60px] md:text-[72px] lg:text-[80px] text-espresso">
                Needle arts,
                <br />
                {" "}chosen <span className="italic text-forest">with care.</span>
              </h1>
              <p className="mt-6 max-w-lg text-base md:text-lg leading-relaxed text-ink">
                Thoughtfully chosen yarn, tools, and inspiration for makers at
                every experience level, online and in Las Vegas.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4">
                <a
                  href={SITE_CONFIG.links.shopify}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-primary-cta"
                  className="btn-primary"
                >
                  Shop Online
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.7} />
                </a>
                <Link
                  to="/visit"
                  data-testid="hero-secondary-cta"
                  className="btn-tertiary"
                >
                  Visit the Shop
                  <ArrowRight className="w-4 h-4" strokeWidth={1.7} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="aspect-[16/10] lg:aspect-[5/4] max-h-[560px] overflow-hidden bg-oat">
                <img
                  src={IMAGES.hero}
                  alt="Hands knitting with colorful yarn"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="home-categories" className="bg-bone border-y border-rule py-20 md:py-28">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 mb-10 md:mb-14">
            <h2 className="lg:col-span-5 font-sans text-3xl md:text-4xl font-semibold leading-tight">
              What we carry
            </h2>
            <p className="lg:col-span-5 lg:col-start-8 text-ink leading-relaxed max-w-xl">
              A focused selection for knitting, crochet, and other needle arts.
              The online assortment and the Booth C25 selection may vary.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8">
            {WHAT_WE_CARRY.map((category) => (
              <Link
                key={category.id}
                to={category.href}
                data-testid={`carry-${category.id}`}
                className="group block"
              >
                <div className="aspect-[4/3] overflow-hidden bg-oat">
                  <img
                    src={category.image}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-5 flex items-start justify-between gap-5">
                  <div>
                    <h3 className="font-sans text-xl font-semibold leading-tight">
                      {category.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink">
                      {category.line}
                    </p>
                  </div>
                  <ArrowRight
                    className="w-4 h-4 mt-1 shrink-0 text-forest"
                    strokeWidth={1.7}
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-story" className="py-20 md:py-28">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <div className="aspect-[4/3] overflow-hidden bg-oat">
                <img
                  src={IMAGES.handsWorking}
                  alt="A maker working with yarn"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-5 lg:col-start-8">
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">
                A local shop,
                <br />
                {" "}<span className="italic text-clay">made by makers.</span>
              </h2>
              <div className="mt-6 space-y-4 text-ink leading-relaxed">
                <p>
                  Cactus Rose is an independent needle-arts shop created by Derek
                  and Brent for the Las Vegas maker community.
                </p>
                <p>
                  Whether you are choosing materials for a familiar project or
                  beginning your first one, the shop is a welcoming place to ask,
                  explore, and find what feels right.
                </p>
                <p>
                  Shop online through Shopify or visit Derek and Brent at Booth
                  {" "}{SITE_CONFIG.location.booth} inside the Fantastic Indoor Swap Meet.
                </p>
              </div>
              <Link to="/about" className="btn-tertiary mt-7" data-testid="home-about-cta">
                Read Our Story
                <ArrowRight className="w-4 h-4" strokeWidth={1.7} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="home-visit" className="bg-cactus-wash border-y border-rule py-20 md:py-28">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <h2 className="font-sans text-3xl md:text-4xl font-semibold leading-tight">
                Visit Booth {SITE_CONFIG.location.booth}
              </h2>
              <p className="mt-4 text-ink leading-relaxed max-w-lg">
                Find Cactus Rose inside the Fantastic Indoor Swap Meet, a local
                Las Vegas destination with plenty to discover under one roof.
              </p>

              <dl className="mt-8 border-t border-forest/25">
                <div className="grid grid-cols-[24px_1fr] gap-3 py-5 border-b border-forest/20">
                  <MapPin className="w-4 h-4 mt-0.5 text-forest" strokeWidth={1.7} />
                  <div>
                    <dt className="sr-only">Address</dt>
                    <dd className="text-sm leading-relaxed">
                      {SITE_CONFIG.location.addressLine1}
                      <br />
                      {SITE_CONFIG.location.addressLine2}
                      <br />
                      {SITE_CONFIG.location.venue}
                    </dd>
                  </div>
                </div>
                <div className="grid grid-cols-[24px_1fr] gap-3 py-5 border-b border-forest/20">
                  <Clock className="w-4 h-4 mt-0.5 text-forest" strokeWidth={1.7} />
                  <div>
                    <dt className="sr-only">Hours</dt>
                    <dd className="text-sm leading-relaxed">
                      {SITE_CONFIG.hours.openDays}
                      <br />
                      {SITE_CONFIG.hours.openTime}
                      <br />
                      {SITE_CONFIG.hours.closedDays}
                    </dd>
                  </div>
                </div>
                <div className="grid grid-cols-[24px_1fr] gap-3 py-5">
                  <Phone className="w-4 h-4 mt-0.5 text-forest" strokeWidth={1.7} />
                  <div>
                    <dt className="sr-only">Phone</dt>
                    <dd className="text-sm">
                      <a href={SITE_CONFIG.phone.href} className="hover:text-clay">
                        {SITE_CONFIG.phone.display}
                      </a>
                    </dd>
                  </div>
                </div>
              </dl>

              <div className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-4">
                <a
                  href={SITE_CONFIG.links.directions}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                  data-testid="home-directions-cta"
                >
                  Get Directions
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.7} />
                </a>
                <Link to="/visit" className="btn-tertiary">
                  Visit Details
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="aspect-[4/3] overflow-hidden bg-oat">
                <img
                  src={IMAGES.store}
                  alt="Yarn and needle-arts supplies in the shop"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-ink">
                {SITE_CONFIG.inventory.notice}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
